import { useCallback, useRef } from 'react';
import { UserRequest, RequestStatus } from '../types';
import { api } from '../services/api';

interface UseRequestPollingProps {
  onRequestUpdate: (request: UserRequest) => void;
  onRequestComplete: (request: UserRequest) => void;
  onRequestFailed: (request: UserRequest) => void;
}

export const useRequestPolling = ({
  onRequestUpdate,
  onRequestComplete,
  onRequestFailed
}: UseRequestPollingProps) => {
  const pollingIntervals = useRef<Map<string, number>>(new Map());

  const startPolling = useCallback((request: UserRequest) => {
    // Clear any existing polling for this request
    stopPolling(request.id);

    const pollInterval = window.setInterval(async () => {
      try {
        console.log('Polling request status for:', request.id);
        const updatedRequest = await api.getUserRequest(request.id);
        
        console.log('Updated request status:', updatedRequest.status);
        
        // Update the request in the UI
        onRequestUpdate(updatedRequest);

        // Check if the request is complete
        if (updatedRequest.status === RequestStatus.Completed || String(updatedRequest.status).toLowerCase() === 'completed') {
          console.log('Request completed:', request.id);
          stopPolling(request.id);
          onRequestComplete(updatedRequest);
        } else if (updatedRequest.status === RequestStatus.Failed || String(updatedRequest.status).toLowerCase() === 'failed') {
          console.log('Request failed:', request.id);
          stopPolling(request.id);
          onRequestFailed(updatedRequest);
        }
      } catch (error) {
        console.error('Error polling request status:', error);
        // Continue polling even if there's an error
      }
    }, 2000); // Poll every 2 seconds

    pollingIntervals.current.set(request.id, pollInterval);
  }, [onRequestUpdate, onRequestComplete, onRequestFailed]);

  const stopPolling = useCallback((requestId: string) => {
    const interval = pollingIntervals.current.get(requestId);
    if (interval) {
      clearInterval(interval);
      pollingIntervals.current.delete(requestId);
    }
  }, []);

  const stopAllPolling = useCallback(() => {
    pollingIntervals.current.forEach((interval) => {
      clearInterval(interval);
    });
    pollingIntervals.current.clear();
  }, []);

  return {
    startPolling,
    stopPolling,
    stopAllPolling
  };
}; 